import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"

const inputVariants = cva(
    "peer border-input file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground flex h-12 min-w-0 rounded-md border bg-[var(--input-background)] px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 mb-4",
    {
        variants: {
            hasError: {
                true: "border-red-500 focus:border-red-500 focus:ring-red-500 ring-red-500 ring-[0.25px]",
                false: "focus:border-transparent focus:[var(--input-highlight-border)] focus:ring-[var(--input-highlight-border)] focus:ring-[0.25px]"
            },
            fullWidth: {
                true: "w-full",
                false: ""
            },
            withIcon: {
                true: "ps-11",
                false: "px-3"
            }
        },
        defaultVariants: {
            hasError: false,
            fullWidth: false,
            withIcon: false
        }
    }
)

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>, VariantProps<typeof inputVariants> {
    label?: string
    errorMessage?: string
    icon?: React.ReactNode
    suffixIcon?: React.ReactNode
    onChange: (value: string) => void
    onEnter?: () => void
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(({
                                                                  label,
                                                                  icon,
                                                                  suffixIcon,
                                                                  className,
                                                                  errorMessage,
                                                                  hasError,
                                                                  fullWidth,
                                                                  onChange,
                                                                  onEnter,
                                                                  ...props
                                                              }, ref) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && onEnter) {
            onEnter()
        }
    }

    const id = props.id || label?.toLowerCase().replace(/\s+/g, "-")

    return (
        <div className="w-full">
            {label && (
                <Label htmlFor={id} className="mb-2 font-medium text-[16px] block">
                    {label}
                </Label>
            )}
            <div className="relative flex items-center">
                {icon && (
                    <div className="absolute mb-4 inset-y-0 left-0 flex items-center ps-4 pointer-events-none">
                        {icon}
                    </div>
                )}
                <input
                    id={id}
                    ref={ref}
                    className={cn(
                        inputVariants({
                            hasError,
                            fullWidth,
                            withIcon: !!icon,
                            className
                        })
                    )}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => onChange(e.target.value)}
                    {...props}
                />

                {suffixIcon && !hasError && (
                    <div className="absolute inset-y-0 end-0 flex items-center pe-3">
                        {suffixIcon}
                    </div>
                )}

                {hasError && (
                    <div className="absolute inset-y-0 end-0 flex items-center pe-3">
                        <svg className="shrink-0 size-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                             viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                    </div>
                )}
            </div>

            {hasError && errorMessage && (
                <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
            )}
        </div>
    )
})
Input.displayName = "Input"

export { Input, inputVariants }
